// Refined checkout modal — glass card on dimmed canvas. The öğrenci (student)
// was chosen in the StudentGate; here the veli (guardian) gives their contact
// details (name, phone, e-mail, optional note) and KVKK consent.

import { ApiRequestError, api } from '@/lib/api.ts';
import { getSessionToken } from '@/lib/session.ts';
import type { SeatDto } from '@okn/shared';
import { MVP_EVENT_ID, formatTrPhone, webReservationCustomerSchema } from '@okn/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  selected: SeatDto[];
  /** Öğrenci chosen in the StudentGate; the reservation is stored under this name. */
  studentId: string;
  studentName: string;
  onChangeStudent: () => void;
  onClose: () => void;
  onSuccess: () => void;
};

type FieldErrors = Partial<
  Record<'guardianName' | 'phone' | 'email' | 'note' | 'kvkkAccepted', string>
>;

export function CheckoutForm({
  selected,
  studentId,
  studentName,
  onChangeStudent,
  onClose,
  onSuccess,
}: Props) {
  const navigate = useNavigate();
  const [guardianName, setGuardianName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [kvkk, setKvkk] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setTopError(null);
    const parsed = webReservationCustomerSchema.safeParse({
      guardianName,
      phone,
      email,
      note: note.trim() || undefined,
      kvkkAccepted: kvkk,
    });
    if (!parsed.success) {
      const fld: FieldErrors = {};
      for (const [k, v] of Object.entries(parsed.error.flatten().fieldErrors)) {
        if (v?.[0]) fld[k as keyof FieldErrors] = v[0];
      }
      setErrors(fld);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await api<{ reservation: { referenceCode: string } }>('/reservations', {
        method: 'POST',
        body: {
          eventId: MVP_EVENT_ID,
          seatIds: selected.map((s) => s.id),
          sessionToken: getSessionToken(),
          studentId,
          customer: parsed.data,
        },
      });
      onSuccess();
      navigate(`/onay/${encodeURIComponent(res.reservation.referenceCode)}`);
    } catch (err) {
      if (err instanceof ApiRequestError) setTopError(err.body.error.message);
      else setTopError('Beklenmedik bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-6"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
      >
        <motion.form
          key="card"
          initial={{ y: 24, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 12, opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 280, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          onSubmit={submit}
          className="modal-card relative max-h-[90dvh] w-full max-w-[520px] overflow-y-auto rounded-2xl"
        >
          {/* Header */}
          <header className="relative overflow-hidden border-b border-border px-8 pb-6 pt-7">
            <div className="kicker mb-2 text-accent">Veli Bilgileri</div>
            <h2 id="checkout-title" className="display text-2xl font-light tracking-tight">
              Ön rezervasyonu tamamla
            </h2>
            <p className="mt-2 text-[13px] text-text-muted">
              {selected.length} koltuk seçtiniz. Onay görüşmesi Acar Sanat Akademi'de yapılır.
            </p>

            <button
              type="button"
              onClick={onClose}
              aria-label="Kapat"
              className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface hover:text-text"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </header>

          {/* Fields */}
          <div className="space-y-4 px-8 py-7">
            {/* Selected öğrenci — read-only recap from the StudentGate */}
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3.5 py-3">
              <div className="min-w-0">
                <div className="kicker mb-1">Öğrenci</div>
                <div className="truncate text-[15px] text-text">{studentName}</div>
              </div>
              <button
                type="button"
                onClick={onChangeStudent}
                className="shrink-0 rounded-md border border-border px-2.5 py-2 text-[11px] text-text-muted transition-colors hover:border-border-strong hover:text-text"
              >
                Değiştir
              </button>
            </div>

            <Field label="Veli Adı Soyadı" error={errors.guardianName} required>
              <input
                type="text"
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                className="input"
                placeholder="Velinin adı soyadı"
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Veli Telefon" error={errors.phone} required>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(formatTrPhone(e.target.value))}
                  maxLength={15}
                  className="input tnum"
                  placeholder="0555 123 45 67"
                />
              </Field>
              <Field label="Veli E-posta" error={errors.email} required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="ornek@mail.com"
                />
              </Field>
            </div>

            <Field label="Not" error={errors.note} optional>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="input resize-none"
                placeholder="Eklemek istedikleriniz…"
              />
            </Field>

            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-md border border-border bg-surface px-3.5 py-3 text-[13px] text-text-muted transition-colors hover:border-border-strong">
              <input
                type="checkbox"
                checked={kvkk}
                onChange={(e) => setKvkk(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span className="leading-snug">
                <span className="text-text">KVKK</span> kapsamında iletişim bilgilerimin rezervasyon
                yönetimi amacıyla kullanılmasını kabul ediyorum.
              </span>
            </label>
            {errors.kvkkAccepted && (
              <p className="-mt-1 text-[12px] text-accent">{errors.kvkkAccepted}</p>
            )}
          </div>

          {topError && (
            <div role="alert" className="border-t border-accent/30 bg-accent-faint px-8 py-3.5">
              <div className="kicker mb-1 text-accent">Hata</div>
              <div className="text-[12px] text-text-muted">{topError}</div>
            </div>
          )}

          {/* Footer */}
          <footer className="flex items-center justify-between gap-3 border-t border-border px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-[13px] uppercase tracking-[0.18em] text-text-faint transition-colors hover:text-text disabled:opacity-50"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-md bg-accent px-6 py-3 text-[13px] font-medium tracking-wide text-white transition-all hover:bg-accent-hover hover:shadow-[0_8px_24px_-8px_var(--accent)] disabled:opacity-50"
            >
              {submitting ? 'Gönderiliyor…' : 'Ön Rezervasyonu Tamamla'}
              <span
                aria-hidden
                className="text-[14px] transition-transform group-hover:translate-x-0.5"
              >
                →
              </span>
            </button>
          </footer>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({
  label,
  error,
  required,
  optional,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  // biome-ignore lint/a11y/noLabelWithoutControl: control passed via children
  return (
    <label className="block">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="kicker">{label}</span>
        {required && <span className="text-[10px] text-accent">●</span>}
        {optional && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-text-faint">opsiyonel</span>
        )}
      </div>
      {children}
      {error && <p className="mt-1 text-[12px] text-accent">{error}</p>}
    </label>
  );
}
